/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Euro, Sparkles, Star } from 'lucide-react';
import { initials } from '@/lib/utils-cat';
import VerifiedBadge from '@/components/VerifiedBadge';

const PriceBadge = ({ isFree, price }) => {
  const { t } = useTranslation();
  return (
    <Badge variant={isFree ? 'success' : 'secondary'} data-testid="booking-price-badge">
      {isFree ? (
        <><Sparkles className="h-3 w-3 mr-1" />{t('telemedFreeTag')}</>
      ) : (
        <><Euro className="h-3 w-3 mr-1" />{price?.toFixed(2)}</>
      )}
    </Badge>
  );
};

/** Intestazione del wizard di prenotazione: avatar, nome, specialità, rating, prezzo. */
export const DoctorHero = ({ doctor, isFree }) => {
  const rating = doctor.rating || 4.8;
  const reviews = doctor.reviews_count || 128;
  return (
    <div className="relative bg-hero-gradient p-5 border-b border-border" data-testid="booking-doctor-hero">
      <div className="flex items-start gap-4">
        <Avatar className="h-16 w-16 ring-4 ring-background shadow-elev-md">
          {doctor.avatar_base64 && <AvatarImage src={doctor.avatar_base64} />}
          <AvatarFallback className="bg-primary-gradient text-primary-foreground font-bold">
            {initials(doctor.full_name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-xl truncate flex items-center gap-1.5">
              <span className="truncate">Dr. {doctor.full_name}</span>
              {doctor.albo_verified && (
                <VerifiedBadge
                  verified
                  size="md"
                  albo={{ number: doctor.albo_number, province: doctor.albo_province }}
                  testId="booking-doctor-verified"
                />
              )}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2 flex-wrap">
              {doctor.specialty && (
                <span className="text-foreground/80 font-medium">{doctor.specialty}</span>
              )}
              <span className="flex items-center gap-0.5 text-amber-500">
                <Star className="h-3.5 w-3.5 fill-current" />
                <span className="text-foreground font-semibold text-sm">{rating}</span>
                <span className="text-muted-foreground text-xs">({reviews})</span>
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <PriceBadge isFree={isFree} price={doctor.visit_price_eur} />
            <span className="text-xs text-muted-foreground">{doctor.slot_minutes || 30} min</span>
          </div>
        </div>
      </div>
    </div>
  );
};
